import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  type CollectionReference,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { wordleLeaderboardDocumentConverter } from "@kekkle/shared/convertors/web/wordle-leaderboard-document.converter";
import type { WordleLeaderboardDocument } from "@kekkle/shared/types/documents/wordle-leaderboard-document/wordle-leaderboard-document";
import type {
  FetchLeaderBoardOptions,
  FetchLeaderBoardResult,
} from "@/types/firestore/wordle";

/**
 * Returns a reference to the wordle_leaderboard collection with converter
 */
function wordleLeaderboardCollection(): CollectionReference<WordleLeaderboardDocument> {
  return collection(firestore, "wordle_leaderboard").withConverter(
    wordleLeaderboardDocumentConverter,
  );
}

/**
 * Fetches the wordle leaderboard, and filters on the userFilter
 * @param userFilter - Array of user IDs to filter by
 * @param options - Pagination options
 * @returns Object containing documents, last document snapshot, and hasMore flag
 */
export async function fetchLeaderBoard(
  userFilter: string[] = [],
  options: FetchLeaderBoardOptions = {},
): Promise<FetchLeaderBoardResult> {
  const { pageSize = 10, lastDoc } = options;
  let docs: WordleLeaderboardDocument[] = [];
  let finalLastDoc: QueryDocumentSnapshot<DocumentData> | undefined;

  // Firestore only supports up to 30 items in a single 'in' query
  // Split the userFilter into batches of 25 to stay well under the limit
  const userFilterBatch: string[][] = [];

  // Split the userFilter into multiple arrays in the userFilterBatch array
  for (let i = 0; i < userFilter.length; i += 25) {
    userFilterBatch.push(userFilter.slice(i, i + 25));
  }

  // Loop through the userFilterBatch array
  // If no filter is provided, run the query once without filtering
  const iterations = userFilterBatch.length > 0 ? userFilterBatch.length : 1;

  for (let i = 0; i < iterations; i++) {
    let scoresQuery = query(
      wordleLeaderboardCollection(),
      orderBy("new_score", "desc"),
    );

    if (userFilterBatch[i]) {
      scoresQuery = query(
        scoresQuery,
        where("user_id", "in", userFilterBatch[i]),
      );
    }

    if (lastDoc && i === 0) {
      scoresQuery = query(scoresQuery, startAfter(lastDoc));
    }

    scoresQuery = query(scoresQuery, limit(pageSize));

    const snapshot = await getDocs(scoresQuery);
    const documents = snapshot.docs.map((doc) => doc.data());
    docs = docs.concat(documents);

    if (snapshot.docs.length > 0) {
      finalLastDoc = snapshot.docs[
        snapshot.docs.length - 1
      ] as QueryDocumentSnapshot<DocumentData>;
    }

    // Only fetch from first batch for pagination
    if (docs.length >= pageSize) break;
  }

  return {
    docs,
    lastDoc: finalLastDoc,
    hasMore: docs.length === pageSize,
  };
}
